// src/admin/pages/banners/BannerManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Drawer,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Stack,
  Tooltip,
  Backdrop,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import { useAuth } from '../../../contexts/AuthContext';
import { useBanners, useBannerMutations } from '../../../hooks/useBanners';
import { Banner } from './banner';
import ToastNotification from '../common/Toastnotification';

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// ─────────────────────────────────────────────────────────────────────────────
// Image Preview Dialog
// ─────────────────────────────────────────────────────────────────────────────
interface ImagePreviewDialogProps {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
}

const ImagePreviewDialog: React.FC<ImagePreviewDialogProps> = ({ open, imageUrl, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <WallpaperIcon color="primary" />
        <Typography variant="h6">Banner Preview</Typography>
      </Stack>
      <IconButton onClick={onClose} edge="end">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ p: 0 }}>
      <Box
        component="img"
        src={imageUrl}
        alt="Banner preview"
        sx={{
          width: '100%',
          maxHeight: '70vh',
          objectFit: 'contain',
          display: 'block',
          bgcolor: 'action.hover',
        }}
      />
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2 }}>
      <Button onClick={onClose} variant="outlined">
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

// ─────────────────────────────────────────────────────────────────────────────
// BannerManagement
// ─────────────────────────────────────────────────────────────────────────────
const BannerManagement: React.FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth(); const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Data ──────────────────────────────────────────────────────────────────
  const {
    data: banners = [],
    isLoading: bannersLoading,
    isFetching: bannersIsFetching,
  } = useBanners();

  const { create, update, remove } = useBannerMutations();

  const loading = bannersLoading || bannersIsFetching;
  const banner: Banner | undefined = banners[0]; // max 1
  const hasBanner = !!banner;

  // ── UI state ──────────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Banner | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const canView = isSuperAdmin || hasPermission('banner.view');
  const canCreate = isSuperAdmin || hasPermission('banner.create');
  const canEdit = isSuperAdmin || hasPermission('banner.edit');
  const canDelete = isSuperAdmin || hasPermission('banner.delete');

  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: ToastState['severity']) => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  // ── Drawer helpers ────────────────────────────────────────────────────────
  const resetForm = () => {
    setImageFile(null);
    setImagePreview('');
    setSelectedBanner(null);
  };

  const handleAddBanner = () => {
    resetForm();
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const handleEditBanner = (b: Banner) => {
    setSelectedBanner(b);
    setImagePreview(b.imageUrl);
    setImageFile(null);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    resetForm();
  };

  // ── Image select ──────────────────────────────────────────────────────────
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image size should be less than 5MB', 'error');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);

    // Reset input so same file can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveSelectedImage = () => {
    setImageFile(null);
    // In edit mode keep the existing URL visible (not removed from DB)
    setImagePreview(drawerMode === 'edit' && selectedBanner ? selectedBanner.imageUrl : '');
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <WallpaperIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary">
              You don't have permission to view banners. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (drawerMode === 'add' && !imageFile) {
      showToast('Please select an image to upload', 'error');
      return;
    }
    if (drawerMode === 'edit' && !imageFile) {
      showToast('Please select a new image to replace the current one', 'warning');
      return;
    }

    try {
      setSaveLoading(true);

      if (drawerMode === 'add') {
        await create.mutateAsync({ userId: currentUser?.uid || '', imageFile: imageFile! });
        showToast('Banner created successfully', 'success');
      } else if (selectedBanner) {
        await update.mutateAsync({ bannerId: selectedBanner.id!, imageFile: imageFile! });
        showToast('Banner updated successfully', 'success');
      }

      setDrawerOpen(false);
      resetForm();
    } catch (err: any) {
      console.error('Error saving banner:', err);
      showToast(`Failed to save banner: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteBanner = (b: Banner) => setDeleteTarget(b);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(true);
      await remove.mutateAsync(deleteTarget.id!);
      setDeleteTarget(null);
      showToast('Banner deleted successfully', 'success');
    } catch (err: any) {
      console.error('Error deleting banner:', err);
      showToast(`Failed to delete banner: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── View ──────────────────────────────────────────────────────────────────
  const handleViewBanner = (b: Banner) => {
    setPreviewUrl(b.imageUrl);
    setPreviewOpen(true);
  };

  // ── Initial loading ───────────────────────────────────────────────────────
  if (bannersLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading banner...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <WallpaperIcon
              sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                Banner Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your site banner image
              </Typography>
            </Box>
          </Stack>

          {/* Add button — hidden when a banner already exists */}
          {!hasBanner && canCreate && (
            <Button
              onClick={handleAddBanner}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
            >
              Add Banner
            </Button>
          )}
        </Stack>

        {/* ── Stats Card ── */}
        {/* <Box sx={{ mb: 3 }}>
          <Card sx={{ maxWidth: 240 }}>
            <CardContent
              sx={{
                p: { xs: 1.5, sm: 2 },
                '&:last-child': { pb: { xs: 1.5, sm: 2 } },
              }}
            >
              <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                Banner Status
              </Typography>
              <Typography
                variant="h5"
                component="div"
                color={hasBanner ? 'success.main' : 'text.disabled'}
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: 0.5 }}
              >
                {hasBanner ? 'Active' : 'None'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {hasBanner ? '1 banner uploaded' : 'No banner yet'}
              </Typography>
            </CardContent>
          </Card>
        </Box> */}
      </Box>

      {/* ── Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {!hasBanner && !loading ? (
          /* Empty state */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
            }}
          >
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <ImageIcon
                  sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No Banner Found
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 360, mx: 'auto' }}
                >
                  You haven't uploaded a banner yet. Add one to display it on your site.
                </Typography>
                {canCreate && (
                  <Button onClick={handleAddBanner} variant="contained" startIcon={<AddIcon />}>
                    Add Banner
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        ) : (
          /* Table */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 'auto', md: 0 },
            }}
          >
            <TableContainer
              component={Paper}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 400, md: 700 } },
              }}
            >
              {(loading || deleteLoading) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 10,
                  }}
                >
                  <LinearProgress />
                </Box>
              )}

              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Banner Image</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      Uploaded
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      Last Updated
                    </TableCell>
                    {(canView || canEdit || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}                  </TableRow>
                </TableHead>
                <TableBody>
                  {banners.map((b) => (
                    <TableRow
                      key={b.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      {/* Thumbnail */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box
                            component="img"
                            src={b.imageUrl}
                            alt="Banner"
                            sx={{
                              width: { xs: 60, sm: 100 },
                              height: { xs: 36, sm: 56 },
                              borderRadius: 1,
                              objectFit: 'cover',
                              flexShrink: 0,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'action.hover',
                            }}
                          />
                          <Box minWidth={0}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              }}
                              noWrap
                            >
                              Site Banner
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              Active banner image
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Uploaded date */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {b.createdAt?.toLocaleDateString() ?? '—'}
                        </Typography>
                      </TableCell>

                      {/* Updated date */}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {b.updatedAt?.toLocaleDateString() ?? '—'}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      {(canView || canEdit || canDelete) && (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                            {canView && (
                              <Tooltip title="View">
                                <IconButton onClick={() => handleViewBanner(b)} size="small" color="info">
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton onClick={() => handleEditBanner(b)} size="small" color="primary">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeleteBanner(b)}
                                  disabled={deleteLoading}
                                  size="small" color="error"
                                >
                                  {deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>

      {/* ── Add / Edit Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drawer header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <WallpaperIcon color="primary" />
              <Typography variant="h6">
                {drawerMode === 'add' ? 'Add Banner' : 'Replace Banner Image'}
              </Typography>
            </Stack>
            <IconButton onClick={handleCloseDrawer} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Drawer body */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
            {/* Upload zone */}
            <Box
              sx={{
                border: '2px dashed',
                borderColor: imagePreview ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                transition: 'border-color 0.2s',
              }}
            >
              {imagePreview ? (
                <Box>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      maxHeight: 280,
                      objectFit: 'contain',
                      borderRadius: 1,
                      mb: 2,
                      bgcolor: 'action.hover',
                    }}
                  />

                  <Stack direction="row" spacing={1.5} justifyContent="center">
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      size="small"
                    >
                      Change Image
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageSelect}
                      />
                    </Button>

                    {/* Only allow removing the newly selected preview; in edit mode
                        we revert to the existing saved image, not a blank state */}
                    {imageFile && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<CloseIcon />}
                        size="small"
                        onClick={handleRemoveSelectedImage}
                      >
                        Undo Selection
                      </Button>
                    )}
                  </Stack>

                  {imageFile && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1.5 }}
                    >
                      New file selected: <strong>{imageFile.name}</strong> (
                      {(imageFile.size / 1024).toFixed(0)} KB)
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box>
                  <ImageIcon
                    sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5, opacity: 0.6 }}
                  />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    Upload a banner image
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 2.5 }}
                  >
                    Accepted formats: JPG, PNG, GIF, WebP · Max size: 5MB
                  </Typography>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                  >
                    Choose Image
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageSelect}
                    />
                  </Button>
                </Box>
              )}
            </Box>

            {drawerMode === 'edit' && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 2 }}
              >
                * Selecting a new image will replace the current banner permanently after
                saving.
              </Typography>
            )}
          </Box>

          {/* Drawer footer */}
          <Divider />
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              gap: 1.5,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            <Button
              onClick={handleCloseDrawer}
              disabled={saveLoading}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={saveLoading || !imageFile}
              startIcon={
                saveLoading ? <CircularProgress size={16} /> : <SaveIcon />
              }
            >
              {drawerMode === 'add' ? 'Upload Banner' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── Image Preview Dialog ── */}
      <ImagePreviewDialog
        open={previewOpen}
        imageUrl={previewUrl}
        onClose={() => setPreviewOpen(false)}
      />

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={!!deleteTarget}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Banner</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the banner? This action cannot be undone and the
            image will be permanently removed from storage.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={
              deleteDialogLoading ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen operation overlay ── */}
      <Backdrop
        open={saveLoading || deleteDialogLoading}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 200,
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}
            >
              {deleteDialogLoading
                ? 'Deleting Banner…'
                : drawerMode === 'add'
                  ? 'Uploading Banner…'
                  : 'Updating Banner…'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
            >
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default BannerManagement;